import React, { useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Package,
  Filter,
  Search,
  Eye,
  Globe,
  Building2,
  Film as FilmIcon,
  Plus,
  Camera,
  IndianRupee,
  ShoppingBag,
  Wrench,
  Boxes,
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip';
import { useAssets } from '../hooks/useAssets';
import { useWorkspace } from '../context/WorkspaceContext';
import { ASSET_CATEGORIES, ALL_CATEGORIES, OWNERSHIP_CONFIG } from '../components/gear/gearConstants';
import AssetDetailSheet from '../components/gear/AssetDetailSheet';
import AssetFormDialog from '../components/gear/AssetFormDialog';
import { cn } from '../lib/utils';

const SCOPE_META = {
  account:   { icon: Globe,     label: 'All Workspaces', badge: 'All Workspaces', readonly: true,  modeLabel: 'Browsing' },
  workspace: { icon: Building2, label: 'Workspace',      badge: 'Workspace',      readonly: true,  modeLabel: 'Browsing' },
  film:      { icon: FilmIcon,  label: 'Film',           badge: 'Film',           readonly: false, modeLabel: null },
};

export default function GearPage() {
  const { workspaceId, filmId } = useParams();
  const { currentWorkspace, workspaces } = useWorkspace();

  /* ── Scope ─────────────────────────────────────────────────── */
  const scope = filmId ? 'film' : workspaceId ? 'workspace' : 'account';
  const scopeMeta = SCOPE_META[scope];
  const isReadOnly = scopeMeta.readonly;

  /* ── Local UI state ────────────────────────────────────────── */
  const [ownershipTab, setOwnershipTab] = useState('all'); // all | owned | rented
  const [activeCategories, setActiveCategories] = useState(ALL_CATEGORIES);
  const [searchQuery, setSearchQuery] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [detailAsset, setDetailAsset] = useState(null);

  /* ── Fetch assets ──────────────────────────────────────────── */
  const { data: assets = [], isLoading, error } = useAssets({
    scope,
    workspace_id: workspaceId,
    film_id: filmId,
    ownership: ownershipTab !== 'all' ? ownershipTab : undefined,
  });

  /* ── Filter + search ───────────────────────────────────────── */
  const filteredAssets = useMemo(() => {
    let result = assets;
    if (activeCategories.length < ALL_CATEGORIES.length) {
      result = result.filter((a) => activeCategories.includes(a.category));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          (a.description && a.description.toLowerCase().includes(q)) ||
          (a.vendor_name && a.vendor_name.toLowerCase().includes(q))
      );
    }
    return result;
  }, [assets, activeCategories, searchQuery]);

  /* ── Derived stats ─────────────────────────────────────────── */
  const stats = useMemo(() => {
    const totalValue = assets.reduce((sum, a) => sum + (Number(a.cost_amount) || 0), 0);
    const ownedCount = assets.filter((a) => a.ownership === 'owned').length;
    const rentedCount = assets.filter((a) => a.ownership === 'rented').length;
    const categories = new Set(assets.map((a) => a.category));
    return { totalValue, ownedCount, rentedCount, categoryCount: categories.size };
  }, [assets]);

  /* ── Handlers ───────────────────────────────────────────────── */
  const toggleCategory = useCallback((cat) => {
    setActiveCategories((prev) => {
      if (prev.includes(cat)) {
        const next = prev.filter((c) => c !== cat);
        return next.length === 0 ? ALL_CATEGORIES : next;
      }
      return [...prev, cat];
    });
  }, []);

  const handleAssetClick = useCallback((asset) => {
    setDetailAsset(asset);
  }, []);

  const handleEditAsset = useCallback((asset) => {
    setDetailAsset(null);
    setEditingAsset(asset);
    setFormOpen(true);
  }, []);

  const handleCreateAsset = useCallback(() => {
    setEditingAsset(null);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingAsset(null);
  }, []);

  const wsName = currentWorkspace?.name || workspaces?.find((w) => w.id === workspaceId)?.name || '';

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto w-full">
      {/* ── Hero ───────────────────────────────────────────── */}
      <div className="flex flex-col gap-1">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary">
          Gear Catalog
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              Gear
            </h1>
            {(() => {
              const badge = (
                <Badge
                  variant="outline"
                  data-testid="page-scope-badge"
                  data-scope={scope}
                  data-readonly={isReadOnly ? 'true' : 'false'}
                  className={`gap-1 text-xs ${isReadOnly ? 'border-muted-foreground/30' : 'border-primary/40 text-primary'}`}
                >
                  {isReadOnly ? <Eye className="h-3 w-3" /> : <scopeMeta.icon className="h-3 w-3" />}
                  {scopeMeta.modeLabel ? `${scopeMeta.modeLabel} · ${scopeMeta.badge}` : scopeMeta.badge}
                </Badge>
              );
              return isReadOnly ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex outline-none">{badge}</span>
                  </TooltipTrigger>
                  <TooltipContent>Open a film to add items.</TooltipContent>
                </Tooltip>
              ) : badge;
            })()}
          </div>
          {!isReadOnly && (
            <Button onClick={handleCreateAsset} className="gap-2 shrink-0">
              <Plus className="h-4 w-4" />
              Add Gear
            </Button>
          )}
        </div>
        {scope === 'workspace' && wsName && (
          <p className="text-sm text-muted-foreground">All gear across films in {wsName}</p>
        )}
      </div>

      {/* ── Stat chips ────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatChip icon={Boxes} label="Total Items" value={assets.length} loading={isLoading} />
        <StatChip icon={ShoppingBag} label="Owned" value={stats.ownedCount} loading={isLoading} accent="text-emerald-500" />
        <StatChip icon={Wrench} label="Rented" value={stats.rentedCount} loading={isLoading} accent="text-amber-500" />
        <StatChip icon={IndianRupee} label="Total Value" value={`₹${stats.totalValue.toLocaleString('en-IN')}`} loading={isLoading} accent="text-primary" />
      </div>

      {/* ── Toolbar ───────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-card rounded-xl border p-3">
        {/* Ownership tabs */}
        <Tabs value={ownershipTab} onValueChange={setOwnershipTab}>
          <TabsList className="h-8">
            <TabsTrigger value="all" className="text-xs px-3 h-7">All</TabsTrigger>
            <TabsTrigger value="owned" className="text-xs px-3 h-7">Owned</TabsTrigger>
            <TabsTrigger value="rented" className="text-xs px-3 h-7">Rented</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Search */}
          <div className="relative flex-1 sm:w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search gear..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-xs"
            />
          </div>

          {/* Category filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs shrink-0">
                <Filter className="h-3.5 w-3.5" />
                Category
                {activeCategories.length < ALL_CATEGORIES.length && (
                  <span className="bg-primary text-primary-foreground rounded-full px-1.5 text-[10px] font-bold">
                    {activeCategories.length}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="end">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1">
                Categories
              </p>
              {ALL_CATEGORIES.map((cat) => {
                const cfg = ASSET_CATEGORIES[cat];
                const active = activeCategories.includes(cat);
                return (
                  <button
                    key={cat}
                    onClick={() => toggleCategory(cat)}
                    className={`flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm transition-colors ${
                      active ? 'bg-accent' : 'opacity-50 hover:opacity-75'
                    }`}
                  >
                    <span className="text-base">{cfg.emoji}</span>
                    {cfg.label}
                  </button>
                );
              })}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* ── Content ───────────────────────────────────────── */}
      {isLoading ? (
        <GearSkeleton />
      ) : error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-center">
          <p className="text-destructive font-medium">Failed to load gear</p>
          <p className="text-sm text-muted-foreground mt-1">{error.response?.data?.detail || error.message}</p>
        </div>
      ) : filteredAssets.length === 0 ? (
        <div className="rounded-xl border bg-card p-12 text-center">
          <Package className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
          <p className="font-display text-xl font-semibold">
            {searchQuery || activeCategories.length < ALL_CATEGORIES.length
              ? 'No matching gear'
              : 'No gear yet'}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {isReadOnly
              ? 'Gear added at the film level will appear here.'
              : 'Click "Add Gear" to register your first piece of equipment.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAssets.map((asset) => (
            <GearCard key={asset.id} asset={asset} onClick={() => handleAssetClick(asset)} />
          ))}
        </div>
      )}

      {/* ── Detail sheet ─────────────────────────────────── */}
      <AssetDetailSheet
        asset={detailAsset}
        open={!!detailAsset}
        onClose={() => setDetailAsset(null)}
        onEdit={!isReadOnly ? handleEditAsset : undefined}
        isReadOnly={isReadOnly}
      />

      {/* ── Create/Edit dialog ───────────────────────────── */}
      {!isReadOnly && (
        <AssetFormDialog
          open={formOpen}
          onClose={handleFormClose}
          filmId={filmId}
          asset={editingAsset}
        />
      )}
    </div>
  );
}

/* ── Stat chip ───────────────────────────────────────────────── */
function StatChip({ icon: Icon, label, value, loading, accent }) {
  return (
    <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
      <div className="rounded-lg bg-muted/50 p-2">
        <Icon className={cn('h-5 w-5', accent || 'text-muted-foreground')} />
      </div>
      <div>
        {loading ? (
          <Skeleton className="h-6 w-12" />
        ) : (
          <p className="text-lg font-bold">{value}</p>
        )}
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      </div>
    </div>
  );
}

/* ── Gear card ───────────────────────────────────────────────── */
function GearCard({ asset, onClick }) {
  const catCfg = ASSET_CATEGORIES[asset.category] || ASSET_CATEGORIES.general;
  const ownCfg = OWNERSHIP_CONFIG[asset.ownership] || OWNERSHIP_CONFIG.owned;

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-xl border bg-card p-4 transition-all hover:ring-1 hover:ring-primary/30 hover:shadow-sm group"
    >
      <div className="flex items-start gap-3">
        {/* Category icon */}
        <div className={cn('flex items-center justify-center rounded-lg h-11 w-11 shrink-0 text-lg', catCfg.light)}>
          {catCfg.emoji}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={cn('text-[10px] font-bold uppercase tracking-wider', catCfg.text)}>
              {catCfg.label}
            </span>
            <Badge variant="outline" className={cn('text-[9px] h-4 px-1.5', ownCfg.light)}>
              {ownCfg.label}
            </Badge>
          </div>
          <p className="font-semibold text-sm truncate group-hover:text-primary transition-colors">
            {asset.name}
          </p>
          {asset.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {asset.description}
            </p>
          )}
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            {asset.cost_amount != null && Number(asset.cost_amount) > 0 && (
              <span className="flex items-center gap-1 font-medium">
                <IndianRupee className="h-3 w-3" />
                {Number(asset.cost_amount).toLocaleString('en-IN')}
                {asset.ownership === 'rented' && <span className="text-[10px]">/day</span>}
              </span>
            )}
            {asset.vendor_name && (
              <span className="truncate max-w-[140px]">{asset.vendor_name}</span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

/* ── Loading skeleton ────────────────────────────────────────── */
function GearSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[...Array(6)].map((_, i) => (
        <div key={`sk-gear-${i}`} className="rounded-xl border bg-card p-4">
          <div className="flex gap-3">
            <Skeleton className="h-11 w-11 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
